type IToggleItem = {
  prevArr: any[];
  item: any;
  type?: "radio" | "multi-select" | "checkbox" | "defaultValue" | "kind";
};

// 过滤 formParams 中的 itemId 在存入最新点击的 radio 值
const filterRadio = ({ prevArr, item, type }: IToggleItem) => {
  let isFind = prevArr.some((val) => val.itemId === item.itemId && item?.value === val.value);
  if (isFind && type === "radio") return prevArr;

  let prevFilterArr = prevArr.filter((val) => val.itemId !== item.itemId && item?.typeFiltrate !== item.itemId);
  // 父级切换过滤所有子类目的
  if (!item?.parentItemId) {
    prevFilterArr = prevFilterArr.filter((val) => val.parentItemId !== item.itemId);
  }

  // 存在则过滤移除，不存在则添加新对象
  return [...prevFilterArr, item];
};

// 过滤 formParams 中的 itemId 在存入最新点击的 checkbox 值
const filterMultiSelect = ({ prevArr, items }: any) => {
  // 获取 items 中所有 parentItemId 的集合
  const itemParentIds = new Set(items.map((item: any) => item.itemId));

  // 过滤掉 prevArr 中与 items 的 itemId 匹配的元素
  const filteredPrevArr = prevArr.filter((prev: any) => !itemParentIds.has(prev.itemId));

  // 合并两个数组（过滤后的 prevArr + items）
  return [...filteredPrevArr, ...items];
};

// 单选 Radio 方法
export const toggleItem = ({ prevArr, item, type }: IToggleItem) => {
  let formParams: any = prevArr;
  if (type === "defaultValue") {
    return mergeUnique([...formParams, ...item]);
  }

  if (type === "multi-select") {
    formParams = filterMultiSelect({ prevArr, items: item });
  } else {
    formParams = filterRadio({ prevArr, item, type });
  }
  return formParams;
};

// 判断 itemId ===item.itemId && item.value === value 同时相当 高亮
export const itemIdValueEqual = ({ formParams, itemId, value }: any) => {
  const isExist = formParams.some(
    (item: any) => item.itemId === itemId && item.value === String(value), // 统一类型比较
  );

  return isExist;
};

// 判断 itemId ===item.itemId && item.value === value 同时相当 高亮
export const getValueByItemId = ({ formParams, itemId }: any) => {
  const isExist = formParams.find((item: any) => item?.itemId === itemId);
  return !!isExist ? isExist : false;
};

interface FormParam {
  parentItemId: number;
  value: any;
  type?: string;
}

interface CascaderItem {
  itemId: number;
  options: CascaderOption[];
}

interface CascaderOption {
  value: any;
  label: string;
  children?: CascaderItem;
}

export function getValueCascaderKind({ formParams, itemId, items, parentItemId }): string {
  // 筛选出符合 parentItemId 且 type 为 kind 的元素
  const filtered = formParams.filter((item) => item.parentItemId === parentItemId && item.type === "kind");

  // 按 value 的数值大小升序排序
  filtered.sort((a, b) => Number(a.value) - Number(b.value));

  // 提取 name 并存入数组
  const name = filtered.map((item) => item.name || item.label);
  // 检查数组是否为空或只包含undefined/null值
  if (!name || name.length === 0 || name.every((item) => item === undefined || item === null)) {
    return "";
  }

  return name.join(" ");
}

export function getValueByItemIdCascader({ formParams, item, parentItemId }: any) {
  const name = [];
  let currentItem = item;

  for (const param of formParams) {
    // 在currentItem.options中查找匹配项
    const found = currentItem.options?.find((opt: any) => {
      return parentItemId === param.parentItemId && opt.value === param.value;
    });

    if (found) {
      name.push(found.label);
      if (found.children && Object.keys(found.children).length) {
        currentItem = found.children;
      }
    }
  }

  return name.join(" ");
}

// 辅助函数：递归查找 formParams 中的 itemId 对应的值
function findValueInFormParams(formParams: any[], itemId: number): string | undefined {
  for (const param of formParams) {
    // 直接匹配 itemId
    if (param.itemId === itemId) {
      return param.value;
    }
    // 递归搜索子节点
    if (param.children?.options) {
      const value = findValueInFormParams(param.children.options, itemId);
      if (value !== undefined) return value;
    }
  }
  return undefined;
}

// 辅助函数：递归收集所有 required=1 的 itemId（包括嵌套结构）
function collectRequiredIds(data: any[], formParams: any[]): number[] {
  let requiredIds: number[] = [];

  for (const item of data) {
    // 收集当前层级的 required 字段
    if (item.required === 1) {
      requiredIds.push(item.itemId);
    }

    // 获取当前项的用户选择值
    const value = findValueInFormParams(formParams, item.itemId);
    if (value === undefined) continue;

    // 查找对应的选项并递归收集子项
    const selectedOption = item.options?.find((opt: any) => opt.value === value);
    if (selectedOption?.children) {
      requiredIds = requiredIds.concat(collectRequiredIds([selectedOption.children], formParams));
    }
  }

  return requiredIds;
}

// 主校验逻辑
export const isDisabled = ({ formParams, data }: any): boolean => {
  // 获取所有必填字段的 ID（包括嵌套结构）
  const requiredIds = collectRequiredIds(data, formParams);
  // debugger;
  if (requiredIds.length === 0) {
    return formParams.length === 0;
  }
  // 检查所有必填字段是否已填写
  const allFilled = requiredIds.every((id) => findValueInFormParams(formParams, id) !== undefined);

  return !allFilled;
};

function findDefaultValues({ question, defaultValue, items, parentItemId }: any) {
  const matchedOption = question.options.find((opt: any) => opt.value === defaultValue);
  if (matchedOption) {
    let parentId = parentItemId || question.itemId;

    // 将当前层级的默认选项加入结果
    items.push({
      label: matchedOption.label,
      value: matchedOption.value,
      itemId: question.itemId,
      parentItemId: parentId,
      type: question.type,
      group: matchedOption.group || "",
    });

    // 递归处理子级问题（如果存在嵌套）
    if (matchedOption.children && matchedOption.children.options) {
      collectDefaultItems(
        [matchedOption.children], // 将子级转为数组结构处理
        items,
        parentId, // 传递当前itemId作为父级
      );
    }
  }
}

export function collectDefaultItems(arr: any, items: any = [], parentItemId = null) {
  if (!arr) return;
  arr.forEach((question: any) => {
    if (!!question.defaultValue) {
      const defaultValues = question.defaultValue.split(",");
      defaultValues.map((defaultValue: any) => {
        findDefaultValues({
          question,
          defaultValue,
          items,
          parentItemId,
        });
      });
    }
  });
  return items;
}

// 排除数组中相同的数据
export const mergeUnique = (...arrays) => {
  const map = new Map();

  arrays.flat().forEach((item) => {
    const key = `${item.itemId}_${item.value}`;
    if (!map.has(key)) {
      map.set(key, item);
    }
  });

  return [...map.values()];
};

// 相同 itemId 值应使用字符串逗号拼接方式上传：“第一项，第二项，第三项”
export function mergeItemIdData({ formParams }: any) {
  let arr: { itemId: number; value: any }[] = [];
  formParams.forEach((item: any) => {
    if (item.itemId && "value" in item) {
      arr.push({
        itemId: item.itemId,
        value: item.value,
      });
    }
  });

  // 使用reduce进行分组合并
  const mergedMap = arr.reduce((map, item) => {
    if (map.has(item.itemId)) {
      // 如果已有相同itemId，追加value
      map.get(item.itemId).value += `,${item.value}`;
    } else {
      // 首次出现则直接存储
      map.set(item.itemId, { ...item });
    }
    return map;
  }, new Map());

  // 将Map转换回数组
  const result = Array.from(mergedMap.values());

  return result;
}
/* 结果相同，但更简洁 */

export const generateArr1 = ({ item, parentItemId }) => {
  // 生成index数组
  const indexSet = new Set();
  const brandMap = new Map(); // 用于按首字母分组品牌

  item.options.forEach((brandOption) => {
    // 提取英文品牌名(兼容"奧迪/Audi"和"Audi"两种格式)
    const enName = brandOption.label.split("/").pop().trim();
    const initial = enName[0].toUpperCase();

    indexSet.add(initial);

    // 构建品牌对象
    const brand = {
      label: initial,
      list: [
        {
          itemId: item.itemId,
          id: brandOption.value,
          value: brandOption.value,
          name: brandOption.label,
          parentItemId: parentItemId,
          label: brandOption.label,
          type: "kind",
          image: `https://img.8891.com.tw/mobileapp/bigNew/2_${enName.toUpperCase()}.png?v=20250325`,
          children: brandOption.children,
          childrens: brandOption.children.options.map((model) => ({
            itemId: brandOption.children.itemId,
            type: "kind",
            id: model.value,
            parentItemId: parentItemId,
            value: model.value,
            name: model.label,
            label: model.label,
          })),
        },
      ],
    };

    // 合并相同首字母的品牌
    if (brandMap.has(initial)) {
      brandMap.get(initial).list.push(...brand.list);
    } else {
      brandMap.set(initial, brand);
    }
  });

  // 转成arr1结构
  return {
    index: [...indexSet].sort(), // 按字母排序
    brand: Array.from(brandMap.values()),
    hot: [], // 需要额外数据可自行补充
  };
};
